import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
/* Imported directly rather than through the shared barrel: services import this
   module, and the barrel would drag every shared component in behind it. */
import ConfirmDialog from '../components/shared/ConfirmDialog';

const ConfirmContext = createContext(null);

/* Services and hooks outside the React tree ask for confirmation too, so the
   provider registers itself here for them. Without a provider mounted the
   browser dialog still answers, which keeps non-UI tests working. */
let askOutsideReact = null;

export const confirmDialog = (options) => {
  if (askOutsideReact) {
    return askOutsideReact(options);
  }

  const { message, detail } = normalize(options);
  return Promise.resolve(window.confirm([message, detail].filter(Boolean).join('\n\n')));
};

function normalize(options) {
  return typeof options === 'string' ? { message: options } : (options || {});
}

export const ConfirmProvider = ({ children }) => {
  const [request, setRequest] = useState(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    setRequest({ ...normalize(options), resolve });
  }), []);

  useEffect(() => {
    askOutsideReact = confirm;
    return () => {
      askOutsideReact = null;
    };
  }, [confirm]);

  const settle = (answer) => {
    request?.resolve(answer);
    setRequest(null);
  };

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={ value }>
      {children}
      <ConfirmDialog
        isOpen={ Boolean(request) }
        title={ request?.title }
        message={ request?.message }
        detail={ request?.detail }
        confirmLabel={ request?.confirmLabel }
        cancelLabel={ request?.cancelLabel }
        danger={ request?.danger !== false }
        onConfirm={ () => settle(true) }
        onCancel={ () => settle(false) }
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  return context ? context.confirm : confirmDialog;
};
